import { useState, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useScoreInterview — POST /api/instructor/interview-sim/{id}/score
 *
 * Supports F4 schema (schema_version=3, 24-criteria):
 * {
 *   result: 'positive' | 'reserve' | 'negative',    // REQUIRED
 *   f1_criteria: {                                   // 24 keys, each 1-10 int, NESTED
 *     kp1_camera_gaze, kp2_smile, kp3_posture, kp4_body_language, kp5_energy, kp6_professional_appearance,
 *     il1_diction, il2_voice_tone, il3_fluency, il4_clear_answer, il5_conciseness, il6_persuasion,
 *     ck1_understanding, ck2_example_answer, ck3_profession_awareness, ck4_confidence, ck5_sincerity, ck6_no_memorization,
 *     in1_pronunciation, in2_vocabulary, in3_sentence_structure, in4_fluency, in5_comprehensibility, in6_confidence
 *   },
 *   cv_career_score: 0-10 float,                    // backend field name: cv_career_score
 *   red_flags: string[],                            // 16-value enum list
 *   red_flag_note: string                           // free-text note
 *   // NOTE: legacy `criteria` field is NOT sent for F4
 * }
 *
 * Response (F4):
 * {
 *   status: 'scored', total_score: null, max_score: null, rescore: bool, xp_awarded: int,
 *   schema_version: 3,
 *   ccri_ars: {
 *     ccri_total, ccri_band, ccri_breakdown: { fiziksel_sunum, iletisim_diksiyon, ingilizce,
 *       motivasyon, kriz, ekip, cv_kariyer },
 *     ars_total, ars_band, ars_breakdown: { kamera_durusu, cevap_kalitesi, ozguven,
 *       havayolu_kulturu, ingilizce_cevap, stres, profesyonel_imaj },
 *     formula_version: 'v3'
 *   },
 *   red_flags: [...]
 * }
 *
 * Legacy (schema_version 1/2) response:
 * { status: 'scored', total_score: number, max_score: number }
 */
export function useScoreInterview() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * @param {object} params
   * @param {string} params.sessionId
   * @param {object} params.f1Criteria  - F4: 24-key nested object (keys kp1..in6)
   * @param {string} params.result      - 'positive' | 'reserve' | 'negative'
   * @param {number} params.cvCareerScore - 0-10 float
   * @param {string[]} params.redFlags  - array of flag enum values
   * @param {string} params.redFlagNote - free-text
   * @param {object} [params.criteria]  - LEGACY v2 (13-criteria), optional
   * @param {string} [params.strengths] - LEGACY v2
   * @param {string} [params.development_areas] - LEGACY v2
   * @param {string} [params.feedback]  - LEGACY v2
   */
  const score = useCallback(async ({
    sessionId,
    f1Criteria,
    result,
    cvCareerScore = 5,
    redFlags = [],
    redFlagNote = '',
    // Legacy fields — only sent if f1Criteria is absent
    criteria,
    strengths = '',
    development_areas = '',
    feedback = '',
  } = {}) => {
    setLoading(true);
    setError(null);

    if (!sessionId) {
      setError('session_id is required');
      setLoading(false);
      return null;
    }

    const isF4 = !!f1Criteria;

    // Build request body
    let body;
    if (isF4) {
      body = {
        result,
        f1_criteria: f1Criteria,
        cv_career_score: cvCareerScore,
        red_flags: redFlags,
        red_flag_note: redFlagNote,
      };
    } else {
      // Legacy v2 — keep backward compat
      if (!criteria) {
        setError('f1_criteria or criteria is required');
        setLoading(false);
        return null;
      }
      body = {
        criteria,
        result,
        strengths,
        development_areas,
        feedback,
      };
    }

    // Mock session IDs — short-circuit with F4 stub
    if (String(sessionId).startsWith('mock-')) {
      await new Promise(resolve => setTimeout(resolve, 900));
      setLoading(false);

      if (isF4) {
        // Minimal CCRI/ARS stub so UI can render the result panel in dev
        const vals = Object.values(f1Criteria).map(Number);
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
        const ccri = Math.round(avg * 9.5);
        const ars = Math.round(avg * 8.8 + (cvCareerScore * 0.7));
        return {
          status: 'scored',
          total_score: null,
          max_score: null,
          rescore: false,
          xp_awarded: 50,
          schema_version: 3,
          ccri_ars: {
            ccri_total: Math.min(100, ccri),
            ccri_band: ccri >= 85 ? 'Çok Yüksek Hazırlık' : ccri >= 70 ? 'Yüksek Hazırlık' : ccri >= 55 ? 'Orta Düzey Hazırlık' : 'Düşük Hazırlık',
            ccri_breakdown: {
              fiziksel_sunum: Math.round(avg * 9),
              iletisim_diksiyon: Math.round(avg * 9.2),
              ingilizce: Math.round(avg * 8.8),
              motivasyon: Math.round(avg * 9.5),
              kriz: Math.round(avg * 8.5),
              ekip: Math.round(avg * 9.3),
              cv_kariyer: Math.round(cvCareerScore * 9),
            },
            ars_total: Math.min(100, ars),
            ars_band: ars >= 90 ? 'Olağanüstü Performans' : ars >= 75 ? 'Güçlü Aday' : ars >= 60 ? 'Yeterli Aday' : ars >= 40 ? 'Gelişim Gerekli' : 'Yetersiz',
            ars_breakdown: {
              kamera_durusu: Math.round(avg * 9.1),
              cevap_kalitesi: Math.round(avg * 9),
              ozguven: Math.round(avg * 8.7),
              havayolu_kulturu: Math.round(avg * 9.4),
              ingilizce_cevap: Math.round(avg * 8.5),
              stres: Math.round(avg * 9.2),
              profesyonel_imaj: Math.round(avg * 9.6),
            },
            formula_version: 'v4',
          },
          red_flags: redFlags,
        };
      } else {
        const total = Object.values(criteria).reduce((a, b) => a + Number(b), 0);
        return { status: 'scored', total_score: total, max_score: 65 };
      }
    }

    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/instructor/interview-sim/${sessionId}/score`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === 'object' ? (detail?.message_tr || detail?.message_en) : detail) ||
        err?.response?.data?.message_tr ||
        err.message;
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, score };
}
